import axios from 'axios';



import { client } from '@/shared/lib/client';
import { isInternalDomain } from '@/utils/const';


export interface UploadFileInfo {
  filename: string;
  size: number;
  type?: string;
  progress?: number;
  status: 'uploading' | 'done' | 'error';
  url?: string;
  path?: string;
  id?: number;
  error?: string;
}

// 错误码枚举，调用方负责国际化
export type UploadErrorCode = 'UPLOAD_FAILED' | 'NETWORK_ERROR';

export interface UploadParams {
  file: File;
  signal?: AbortSignal;
  onProgress?: (file: UploadFileInfo) => void;
  onSuccess?: (file: UploadFileInfo) => void;
  onFailed?: (file: UploadFileInfo, errorCode: UploadErrorCode, errorDetail?: string) => void;
}

interface UploadResponse {
  status: number;
  data?: {
    url?: string;
    path?: string;
    id?: number;
    filename?: string;
  };
  message?: string;
}

/**
 * 上传单个文件到服务器
 */
export const uploadFile = async ({
  file,
  signal,
  onProgress,
  onSuccess,
  onFailed,
}: UploadParams): Promise<UploadFileInfo | null> => {
  // 构建 FormData
  const formData = new FormData();
  formData.append('file', file, file.name);

  // 初始化文件信息
  const fileInfo: UploadFileInfo = {
    filename: file.name,
    size: file.size,
    type: file.type,
    status: 'uploading',
    progress: 0,
  };

  // 根据域名选择上传接口
  const uploadUrl = isInternalDomain()
    ? '/eapi/kwaipilot/file/upload'
    : '/api/proxy/upload/eapi/kwaipilot/file/upload';

  try {
    const response = (await client({
      url: uploadUrl,
      method: 'POST',
      data: formData,
      signal,
      onUploadProgress: (e) => {
        const total = e.total || e.loaded;
        const progress = Math.round((e.loaded / total) * 100);
        onProgress?.({
          ...fileInfo,
          progress: Math.min(progress, 100),
          status: 'uploading',
        });
      },
    })) as UploadResponse;

    if (response?.status === 200 && response?.data) {
      const result: UploadFileInfo = {
        ...fileInfo,
        url: response.data.url,
        path: response.data.path,
        id: response.data.id,
        status: 'done',
        progress: 100,
      };
      onSuccess?.(result);
      return result;
    }

    // 上传失败
    const errorResult: UploadFileInfo = {
      ...fileInfo,
      status: 'error',
      error: response?.message,
    };
    onFailed?.(errorResult, 'UPLOAD_FAILED', response?.message);
    return null;
  } catch (err) {
    // 如果是取消请求，不调用 onFailed
    if (axios.isCancel(err) || (err instanceof Error && err.name === 'AbortError')) {
      return null;
    }

    const errorDetail = err instanceof Error ? err.message : undefined;
    const errorResult: UploadFileInfo = {
      ...fileInfo,
      status: 'error',
      error: errorDetail,
    };
    onFailed?.(errorResult, 'NETWORK_ERROR', errorDetail);
    return null;
  }
};